import React, { useState, useEffect } from "react";

import { t } from "i18next";
import { Trans } from "react-i18next";
import Select from "react-select";

import LookupsAPI from "../../API/lookups.api";
import SelectTableForm from "./SelectTableForm";
import {
    getReactSelectStyling,
    getReactSelectTheme,
} from "../../utils/ReactSelectStyles";
import { ROLES } from "../../utils/constants";
import { isEmpty } from "lodash";
import CheckBox from "../CheckBox/CheckBox";

const UserOrganizationForm = ({
    isChecked,
    setIsChecked,
    excludedOrganization,
    selectedOrganizations,
    setSelectedOrganizations,
    mappedOrganizations,
    notificationMessage,
}) => {
    const [fetchedData, setFetchedData] = useState([]);
    const [organizationsLookup, setOrganizationsLookup] = useState([]);
    const [organizationLoaded, setOrganizationLoaded] = useState(false);
    const [userRoleMap, setUserRoleMap] = useState({});

    const headers = [t("affiliateName"), t("role"), t("default"), t("remove")];

    useEffect(() => {
        getOrganizationsLookup().then(() => setOrganizationLoaded(true));
        getUserRoles();
    }, []);

    useEffect(() => {
        handleIsCheckedValue();
    }, [selectedOrganizations]);

    useEffect(() => {
        if (organizationLoaded) applyExistingOrganizations();
    }, [mappedOrganizations, organizationLoaded]);

    useEffect(() => {
        if (!isEmpty(fetchedData)) {
            mapOrganizationLookup();
        }
    }, [fetchedData]);

    const getOrganizationsLookup = async () => {
        const result = await LookupsAPI.getOrganizations();
        setFetchedData(result);
    };

    const getUserRoles = async () => {
        const result = await LookupsAPI.getRoles();
        setUserRoleMap(mapUserRoles(result));
    };

    const mapUserRoles = (roles) => {
        const dict = {};
        roles.forEach((role) => {
            dict[role.role] = role.id;
        });
        return dict;
    };

    const handleIsCheckedValue = () => {
        if (selectedOrganizations.length === 1)
            setIsChecked(selectedOrganizations[0].value.id);
    };

    const applyExistingOrganizations = () => {
        const { selectedOrgs, orgsLookup } = organizationsLookup.reduce(
            (accum, current) => {
                const mappedOrgValue = mappedOrganizations[current.value.id];
                mappedOrgValue
                    ? accum.selectedOrgs.push({
                          ...current,
                          value: {
                              ...current.value,
                              newRole: {
                                  label: mappedOrgValue.role,
                                  value: userRoleMap[mappedOrgValue.role],
                              },
                          },
                      })
                    : accum.orgsLookup.push(current);
                return accum;
            },
            {
                selectedOrgs: [],
                orgsLookup: [],
            }
        );

        setOrganizationsLookup(orgsLookup);
        setSelectedOrganizations(selectedOrgs);
    };

    const mapOrganizationLookup = () => {
        const mapped = fetchedData.map((organization) => ({
            label: organization.name,
            value: organization,
        }));

        const filtered =
            excludedOrganization &&
            mapped.filter(
                (organization) =>
                    organization.value.id !== excludedOrganization.value
            );

        setOrganizationsLookup(filtered || mapped);
    };

    const isExists = (id) => {
        return mappedOrganizations[id] || null;
    };

    const handleUnDeleteExistingAffiliate = (newSelected, organizationId) => {
        let selectedOrganization = newSelected.find(
            (m) => m.value.id === organizationId
        );
        selectedOrganization.value.isDeleted = false;
    };

    const getDefaultRoleValue = (isSubAffiliate) => {
        return isSubAffiliate
            ? { label: ROLES.user, value: userRoleMap[ROLES.user] }
            : {
                  label: ROLES.partnerUser,
                  value: userRoleMap[ROLES.partnerUser],
              };
    };

    const handleAffiliateSelected = (organization) => {
        if (organization) {
            organization.value.newRole = getDefaultRoleValue(
                organization.value.partnerId
            );
            const organizationId = organization.value.id;
            const newSelected = [...selectedOrganizations];

            // handle if the model already exists in the selected model, so we can change the isDeleted flag only
            isExists(organizationId)
                ? handleUnDeleteExistingAffiliate(newSelected, organizationId)
                : newSelected.push(organization);

            // remove the selection from dropdown options
            const filtered = organizationsLookup.filter(
                (currentOrg) => currentOrg.value.id !== organization.value.id
            );

            setOrganizationsLookup(filtered);
            setSelectedOrganizations(newSelected);
        }
    };

    const handleDelete = (index) => {
        let selectedOrganization = selectedOrganizations[index].value;
        const newSelected = [...selectedOrganizations];

        isExists(selectedOrganization?.id)
            ? (selectedOrganization.isDeleted = true)
            : newSelected.splice(index, 1);

        setOrganizationsLookup((prev) => [
            ...prev,
            {
                ...selectedOrganizations[index],
            },
        ]);

        setSelectedOrganizations(newSelected);
        handleDefaultDeleted(selectedOrganization, newSelected, index);
    };

    const handleDefaultDeleted = (selectedOrganization, newSelected, index) => {
        if (selectedOrganization.id === isChecked) {
            const selectedLength = newSelected.length;

            if (selectedLength === 0) {
                setIsChecked("");
            } else if (index === 0) {
                setIsChecked(newSelected[0].value.id);
            } else {
                setIsChecked(newSelected[Math.max(index - 1, 0)].value.id);
            }
        }
    };

    const handleChangeAccessType = (role, index) => {
        const newSelected = [...selectedOrganizations];
        newSelected[index].value = {
            ...newSelected[index].value,
            newRole: role,
        };

        setSelectedOrganizations(newSelected);
    };

    const getVisibleAffiliates = () => {
        return selectedOrganizations.filter(
            (organization) => !organization.value.isDeleted
        );
    };

    const getTableLabel = () => {
        return (
            <>
                {t("addedAffiliates")}
                <span className="text-primary-orange ml-1">
                    ({getVisibleAffiliates().length})
                </span>
            </>
        );
    };

    const getOrganizationsLookupOptions = (isSubAffiliate) => {
        const allowedRoles = isSubAffiliate
            ? [ROLES.admin, ROLES.user]
            : [ROLES.partnerAdmin, ROLES.partnerUser, ROLES.support];

        return Object.values(allowedRoles).map((role) => ({
            label: role,
            value: userRoleMap[role],
        }));
    };

    const keys = [
        {
            name: "label",
        },
        {
            element: (row, index) => {
                return (
                    <div className="px-6 py-3 whitespace-wrap">
                        {row.isDefault ? (
                            row.role
                        ) : (
                            <Select
                                options={getOrganizationsLookupOptions(
                                    row.value.partnerId
                                )}
                                value={row.value.newRole}
                                styles={getReactSelectStyling({
                                    includeMenuStyle: false,
                                })}
                                onChange={(selected) => {
                                    handleChangeAccessType(
                                        selected,
                                        excludedOrganization ? index - 1 : index
                                    );
                                }}
                                theme={getReactSelectTheme}
                                menuPosition="fixed"
                                menuPlacement="auto"
                                menuShouldScrollIntoView={false}
                            />
                        )}
                    </div>
                );
            },
        },
        {
            element: (row, index) => {
                return (
                    <div className="px-6 py-3 whitespace-wrap">
                        {excludedOrganization && !row.isDefault ? (
                            <span className="px-0">---</span>
                        ) : (
                            <CheckBox
                                name={`isDefault-checkbox-${index}`}
                                checked={
                                    row.isDefault || row.value.id === isChecked
                                }
                                onChangeHandler={() =>
                                    setIsChecked(row.value.id)
                                }
                                disabled={excludedOrganization}
                                containerClassName="justify-center"
                            />
                        )}
                    </div>
                );
            },
        },
        {
            element: (row, index) => (
                <div className="px-6 py-3 whitespace-wrap">
                    <button
                        onClick={() =>
                            handleDelete(
                                excludedOrganization ? index - 1 : index
                            )
                        }
                        className={`table-btn table-btn-orange m-0 ${
                            row.isDefault ? "disabled" : ""
                        }`}
                        disabled={row.isDefault}
                    >
                        <i className="fas fa-trash-alt" />
                    </button>
                </div>
            ),
        },
    ];

    const getSelectedData = () => {
        return excludedOrganization
            ? [
                  { ...excludedOrganization, isDefault: true },
                  ...selectedOrganizations,
              ]
            : selectedOrganizations;
    };

    return (
        <>
            <SelectTableForm
                mainSelect={{
                    label: <Trans i18nKey="affiliates">Affiliates</Trans>,
                    options: organizationsLookup,
                    onChangeHandler: handleAffiliateSelected,
                    placeholder: t("affiliatePlaceholder"),
                }}
                tableLabel={getTableLabel()}
                selectedData={getSelectedData()}
                emptySelectedPlaceholder={
                    "Select Affiliate from the dropdown to be added"
                }
                keys={keys}
                headers={headers}
                notificationMessage={notificationMessage}
            />
        </>
    );
};

export default UserOrganizationForm;
